/**
 * battleHubMemory — the Battle Hub's applied state across a round-trip, and
 * the way back to it (2026-09-10 owner ask: a compare box opens the copy's
 * record page, and Back returns to the meta where the trainer entered).
 *
 * TWO THINGS, kept apart:
 *
 *   1. SESSION MEMORY of the hub's applied values (mode, league, measure,
 *      filters, the compare toggles), the exact pattern of dexFilterMemory:
 *      in memory only, survives a navigation-driven remount, resets on a cold
 *      launch. Only applied VALUES are remembered.
 *
 *   2. THE RETURN INTENT. The shell's Back button sends every /my-pokemon/:id
 *      page to the My Pokémon list (that page can be swiped through the
 *      list, so Back means "the list"). A record opened FROM the hub arms an
 *      intent here, and Back consults it: pending means /battle. The intent
 *      dies the moment the route goes anywhere but a record page or the hub,
 *      so a later Back from some other record can never be hijacked by a
 *      stale one. The page offset the trainer left at rides along, so the hub
 *      can put the row back under their thumb.
 */
import { hashGetSnapshot, hashSubscribe } from "./hashRouter";
import { DEFAULT_MEASURE, NO_FILTERS, type MetaFilters } from "./battleMeta";
import { NO_RAID_FILTERS, RAID_MEASURES, type RaidFilters, type RaidMeasureKey } from "./raidMeta";
import { BATTLE_META } from "./generated/battleMeta.generated";

export type BattleHubMode = "pvp" | "raid" | "yours";

export interface BattleHubMemory {
  mode: BattleHubMode;
  pvp: { leagueKey: string; measureKey: string; filters: MetaFilters; compare: boolean };
  raid: { measureKey: RaidMeasureKey; filters: RaidFilters; compare: boolean };
}

function fresh(): BattleHubMemory {
  return {
    mode: "pvp",
    pvp: {
      leagueKey: BATTLE_META[0]?.key ?? "great",
      measureKey: DEFAULT_MEASURE,
      filters: { ...NO_FILTERS },
      compare: false,
    },
    raid: {
      measureKey: RAID_MEASURES[0].key,
      filters: { ...NO_RAID_FILTERS, bossTypes: [] },
      compare: false,
    },
  };
}

let memory: BattleHubMemory = fresh();

export function getBattleHubMemory(): BattleHubMemory {
  return {
    mode: memory.mode,
    pvp: { ...memory.pvp, filters: { ...memory.pvp.filters } },
    raid: { ...memory.raid, filters: { ...memory.raid.filters, bossTypes: [...memory.raid.filters.bossTypes] } },
  };
}

export function setBattleHubMode(mode: BattleHubMode): void {
  memory = { ...memory, mode };
}

export function setBattleHubPvp(next: Partial<BattleHubMemory["pvp"]>): void {
  memory = { ...memory, pvp: { ...memory.pvp, ...next } };
}

export function setBattleHubRaid(next: Partial<BattleHubMemory["raid"]>): void {
  memory = { ...memory, raid: { ...memory.raid, ...next } };
}

// ── The return intent ────────────────────────────────────────────────────────

const HUB_PATH = "/battle";
const RECORD_PREFIX = "/my-pokemon/";

let pendingReturn: { scrollY: number } | null = null;

function safeSnapshot(): string | null {
  try {
    return hashGetSnapshot();
  } catch {
    return null;
  }
}

if (typeof window !== "undefined") {
  hashSubscribe(() => {
    const path = safeSnapshot();
    if (path == null) return;
    // Anywhere but a record page or the hub itself ends the excursion.
    if (path !== HUB_PATH && !path.startsWith(RECORD_PREFIX)) pendingReturn = null;
  });
}

/** Called just before the hub opens a record: Back should come back here. */
export function armBattleHubReturn(scrollY: number): void {
  pendingReturn = { scrollY: Math.max(0, Math.round(scrollY)) };
}

/** True while a record page opened from the hub is the one being left. */
export function battleHubReturnPending(): boolean {
  return pendingReturn != null;
}

/** The hub, on mount: the offset to put back, or null. Clears the intent. */
export function takeBattleHubReturn(): { scrollY: number } | null {
  const out = pendingReturn;
  pendingReturn = null;
  return out;
}

/** Test seam. */
export function __resetBattleHubMemoryForTest(): void {
  memory = fresh();
  pendingReturn = null;
}
